import React from "react"
import { bindActionCreators } from "redux"
import { connect } from "react-redux"
import { push } from "react-router-redux"
import { Route, Link, Switch } from "react-router-dom"
import { Slider, Select, Button, Col, Row } from "antd"

import All from "./All"
import Update from "./Update"
import New from "./New"
import { create, show } from "./actions"
import onMount from "../../core/onMount"

const mapDispatchToProps = dispatch => {
  return bindActionCreators({ create, show }, dispatch)
}

const mapStateToProps = ({
  models: {
    user: { favorite_transactions }
  }
}) => {
  return {
    favorite_transactions
  }
}

export default props => (
  <Switch>
    <Route
      exact
      path={`/account/favorite-transactions`}
      component={connect(
        mapStateToProps,
        mapDispatchToProps
      )(All)}
    />

    <Route path={`/account/favorite-transactions/new`} component={New} />

    <Route
      path={`/account/favorite-transactions/:tokenId`}
      component={Update}
    />
  </Switch>
)

export const getRemainingDays = existingDays => {
  return [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday"
  ].filter(day => !existingDays.includes(day))
}

export const mapOptionLabels = observedFieldsArray => {
  return observedFieldsArray.map(f => ({
    name: {
      // amount: "Amount",
      revenue: "Revenue",
      expense: "Expense",
      merchant: "Merchant",
      category: "Category",
      date: "Date",
      day_of_week: "Day of week",
      institution_account_token_id: "Bank Account"
    }[f],
    value: f
  }))
}

export const isAmountRangeValid = arr => {
  const sorted = arr.sort(
    (a, b) => parseFloat(a.enforced_value) - parseFloat(b.enforced_value)
  )
  return (
    sorted[0].comparison_operator == ">" && sorted[1].comparison_operator == "<"
  )
}

export const getAllowedFields = observedFields => {
  function count(arr) {
    return arr.reduce(
      (prev, curr) => ((prev[curr] = ++prev[curr] || 1), prev),
      {}
    )
  }
  const {
    expense,
    revenue,
    merchant,
    category,
    date,
    day_of_week,
    institution_account_token_id
  } = count(observedFields)

  const allowed = []
  // Using the count totals, add observedField choices based on count totals

  if (!expense && revenue < 2) allowed.push("revenue")

  if (!revenue && expense < 2) allowed.push("expense")

  if (!revenue && !expense) {
    allowed.push("expense")
    allowed.push("revenue")
  }

  if (!merchant || merchant < 1) allowed.push("merchant")

  if (!category || category < 1) allowed.push("category")

  if (!date || date < 1) allowed.push("date")

  if (!day_of_week || day_of_week < 1) allowed.push("day_of_week")

  if (!institution_account_token_id || institution_account_token_id < 1)
    allowed.push("institution_account_token_id")

  return allowed
}

export const amountToExpenseOrRevenue = rule => {
  if (rule.observed_field == "amount") {
    return parseFloat(rule.enforced_value) < 0
      ? {
          ...rule,
          observed_field: "expense",
          enforced_value: String(-parseFloat(rule.enforced_value)),
          comparison_operator:
            rule.comparison_operator == ">" ? "<" : "<" ? ">" : "="
        }
      : {
          ...rule,
          observed_field: "revenue"
        }
  }
  return rule
}

export const revenueOrExpenseToAmount = rule => {
  if (rule.observed_field == "revenue")
    return {
      ...rule,
      observed_field: "amount"
    }

  if (rule.observed_field == "expense")
    return {
      ...rule,
      observed_field: "amount",
      enforced_value: String(-parseFloat(rule.enforced_value)),
      comparison_operator:
        rule.comparison_operator == ">" ? "<" : "<" ? ">" : "="
    }
  // else
  return rule
}

export const accountsFromInstitutionUsers = institutionUsers => {
  const institution_accounts = institutionUsers.map(iu =>
    iu.institution_accounts.map(account => ({
      ...account,
      institution_name: iu.institution_name
    }))
  )

  const accounts = [].concat(...institution_accounts).map(ia => {
    return {
      name: ia.meta["name"],
      displayName: `${ia.meta["name"]} ${ia.meta["number"]}`,
      institution_name: ia.institution_name,
      institution_account_token_id: ia.token_id
    }
  })
  return accounts
}

// export const formatPurpose = (incomeSources, purpose) =>
//   incomeSources.includes(purpose)
//     ? { income_source: purpose, purpose: "Work" }
//     : { income_source: null, purpose }

export const setPurposeAndIncomeSource = optionValue => {
  if (!optionValue) return []

  const nonIncomeSourcePurposes = [
    "Personal",
    "Work",
    "Charity",
    "Medical",
    "Moving",
    "Commute"
  ]
  return nonIncomeSourcePurposes.includes(optionValue)
    ? [
        {
          enforced_field: "purpose",
          enforced_value: optionValue
        }
      ]
    : [
        {
          enforced_field: "purpose",
          enforced_value: "Work"
        },
        {
          enforced_field: "income_source",
          enforced_value: optionValue
        }
      ]
}

export const getCombinedPurpose = rule_actions => {
  const incomeSourceRuleAction = rule_actions.find(
    ruleAction => ruleAction.enforced_field == "income_source"
  )
  const purposeRuleAction = rule_actions.find(
    ruleAction => ruleAction.enforced_field == "purpose"
  )
  return incomeSourceRuleAction
    ? incomeSourceRuleAction.enforced_value
    : purposeRuleAction
      ? purposeRuleAction.enforced_value
      : null
}

export const getPurposes = state => [
  // INCOME SOURCES:
  "Work",
  "Personal",
  ...state.models.user.income_sources,
  // PURPOSES AND CUSTOM PURPOSES:
  "Charity",
  "Medical",
  "Moving",
  "Commute",
  ...(state.models.user.custom_purposes || [])
]

export const onObservedChange = observed_field => {
  switch (observed_field) {
    case "date":
      return {
        observed_field,
        enforced_type: "String",
        start_date: null,
        end_date: null,
        enforced_value: null,
        type: "DateRangeRule",
        comparison_operator: "="
      }

    case "merchant":
    case "category":
      return {
        observed_field,
        enforced_type: "String",
        enforced_value: null,
        type: "EqualityRule",
        comparison_operator: "=~"
      }

    case "expense":
    case "revenue":
      return {
        observed_field,
        enforced_type: "Float",
        enforced_value: null,
        type: "EqualityRule",
        comparison_operator: "="
      }

    case "institution_account_token_id":
    case "day_of_week":

    default:
      return {
        observed_field,
        enforced_type: "String",
        enforced_value: null,
        type: "EqualityRule",
        comparison_operator: "="
      }
  }
}

export const validateForm = (values, props) => {
  console.log({ values, props })
  const errors = {}

  if (
    values.rule_actions &&
    values.rule_actions
      .filter(ra => ra.enforced_value !== null)
      .filter(ra => ra.enforced_value.trim() != "").length < 1
  ) {
    errors.rule_actions = {
      enforced_value: "Either 'Purpose' or 'Notes' is required."
    }
  }

  if (values.rules.length < 1) {
    errors.rules = {
      enforced_value: "Rules cannot be empty"
    }
  }

  values.rules.map((rule, index) => {
    if (!(rule.enforced_value || rule.start_date)) {
      if (!errors.rules) {
        errors.rules = []
      }
      errors.rules[index] = {
        enforced_value: "Cannot be blank "
      }
    }

    const amountRules = values.rules.filter(rule =>
      ["expense", "revenue"].includes(rule.observed_field)
    )
    // console.log({ amountRules, length: amountRules.length })

    if (["expense", "revenue"].includes(rule.observed_field)) {
      if (amountRules.length == 2) {
        if (amountRules.some(r => r.comparison_operator == "=")) {
          if (!errors.rules) {
            errors.rules = []
          }

          errors.rules[index] = {
            enforced_value: "Only one amount is allowed for 'is equal' "
          }
        }
        if (!isAmountRangeValid(amountRules)) {
          if (!errors.rules) errors.rules = []

          errors.rules[index] = {
            enforced_value:
              "Invalid amount range. Greater than value must be smaller than less than value"
          }
        }
      }
    }
    const { enforced_value } = rule

    // if (!enforced_value) {
    //   if (!errors.rules) errors.rules = []
    //   errors.rules[index] = { enforced_value: "Required" }
    // } else if (
    //   // observedFieldValidation(rule.observed_field)
    //   !(!isNaN(parseFloat(enforced_value)) && isFinite(enforced_value))
    // ) {
    //   if (!errors.rules) errors.rules = []
    //   errors.rules[index] = { enforced_value: "Invalid" }
    // }
  })
  return errors
}



// WEBPACK FOOTER //
// ./src/account/favorite-transactions/index.js